/**
 * Machine d'état (pure) des phases du wizard de génération V2.
 *
 * Chaque phase passe par idle → running → done | error. Une phase n'est
 * lançable que si son prérequis est `done`. « Tout enchaîner » parcourt les
 * phases d'autorun (hors image) dans l'ordre. Module pur — testable.
 */

export type PhaseId = 'coherence' | 'solution' | 'audit' | 'distractors' | 'clue_hunt' | 'image';
export type PhaseStatus = 'idle' | 'running' | 'done' | 'error';

export const PHASE_SEQUENCE: readonly PhaseId[] = [
  'coherence',
  'solution',
  'audit',
  'distractors',
  'clue_hunt',
  'image',
];

/** Phases enchaînées par « Tout enchaîner » (l'image et la chasse aux indices
    restent manuelles : la chasse est optionnelle et non pertinente sur tous
    les exercices, on la propose sans l'imposer). */
export const AUTORUN_SEQUENCE: readonly PhaseId[] = [
  'coherence',
  'solution',
  'audit',
  'distractors',
];

/**
 * Prérequis de chaque phase. L'audit et les distracteurs ne dépendent QUE du
 * corrigé (pas de dépendance de données entre eux) : on n'oblige donc pas à
 * lancer l'audit avant les distracteurs — c'est plus tolérant et évite de
 * dépendre d'un état d'audit indéductible des blocs.
 */
export const PHASE_PREREQ: Record<PhaseId, PhaseId | null> = {
  coherence: null,
  solution: 'coherence',
  audit: 'solution',
  distractors: 'solution',
  // Chasse aux indices : besoin de l'énoncé segmenté ET des corrections
  // éventuelles (les answer_md des questions précédentes = sources valides).
  clue_hunt: 'solution',
  image: 'solution',
};

export type PhaseStatuses = Record<PhaseId, PhaseStatus>;

export function initialStatuses(): PhaseStatuses {
  return {
    coherence: 'idle',
    solution: 'idle',
    audit: 'idle',
    distractors: 'idle',
    clue_hunt: 'idle',
    image: 'idle',
  };
}

export function setStatus(
  statuses: PhaseStatuses,
  id: PhaseId,
  status: PhaseStatus,
): PhaseStatuses {
  return { ...statuses, [id]: status };
}

/** Une phase est lançable si elle ne tourne pas et que son prérequis est `done`. */
export function canRunPhase(id: PhaseId, statuses: PhaseStatuses): boolean {
  if (statuses[id] === 'running') return false;
  if (Object.values(statuses).some((s) => s === 'running')) return false; // une seule à la fois
  const prereq = PHASE_PREREQ[id];
  return prereq === null || statuses[prereq] === 'done';
}

/** Prochaine phase d'autorun non terminée, ou null si tout est fait. */
export function nextAutorunPhase(statuses: PhaseStatuses): PhaseId | null {
  for (const id of AUTORUN_SEQUENCE) {
    if (statuses[id] !== 'done') return id;
  }
  return null;
}

/** « Tout enchaîner » est proposable s'il reste au moins une phase d'autorun à faire. */
export function canRunAll(statuses: PhaseStatuses): boolean {
  if (Object.values(statuses).some((s) => s === 'running')) return false;
  return nextAutorunPhase(statuses) !== null;
}

/**
 * Statuts initiaux déduits de l'état des blocs en base (brouillon rouvert).
 * On ne peut pas déduire l'audit des blocs → laissé `idle` (relançable).
 */
export function statusesFromProgress(progress: {
  coherenceDone: boolean;
  solutionDone: boolean;
  distractorsDone: boolean;
  clueHuntDone?: boolean;
}): PhaseStatuses {
  return {
    coherence: progress.coherenceDone ? 'done' : 'idle',
    solution: progress.solutionDone ? 'done' : 'idle',
    audit: 'idle',
    distractors: progress.distractorsDone ? 'done' : 'idle',
    clue_hunt: progress.clueHuntDone ? 'done' : 'idle',
    image: 'idle',
  };
}
